import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { ColumnConfiguratorWidget, ConfirmationModalLayout } from '../../../widgets/layouts';
import * as stateSetup from './assignees.store';
import { ColumnConfiguration } from '../../../models/column-configuration';
import { useAppActions, useAppState } from '../../../overmind';
import { CompletionStatus, FetchType, OrganizationLevelType } from '../../../enums';
import { ResetAssessmentModal } from './assignees.models';
import { Button, DropdownSelect, DropdownSelectOption, Filters, ITableDataRow, KebabMenu, Modal, PageHeader, Pager, Panel, Searchfield, SkillLevel, SkillPercentage, Table, TableColumn, TableRow, scoreLevelRAGColour, scorePercentageRAGColour } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { UsersListSearchParams } from '../../../models/overmind/search-params';
import layoutStyles from '../../../design/layout.module.css';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { CompletionStatusChip } from '../../../components/chips/completion-status.chip';
import { AssessmentAssigneeListItem } from '../../../overmind/actions';

const pageSize = 12;

const statuses: DropdownSelectOption[] = [
  { label: 'Not Started', value: CompletionStatus.NotStarted.toString() },
  { label: 'In Progress', value: CompletionStatus.InProgress.toString() },
  { label: 'Complete', value: CompletionStatus.Completed.toString() },
];

export function AssigneesPage(): JSX.Element {
  const { assessmentSlug } = useParams();

  if (!assessmentSlug) return <EmptyState />;

  const actions = useAppActions();
  const { user, permissions, companyVariables } = useAppState();

  const organizationLevel: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );

  const [selectedStatus, setSelectedStatus] = useState<CompletionStatus>();
  const [selectedRows, setSelectedRows] = useState<ITableDataRow[]>([]);
  const [loadingLearner, setLoadingLearner] = useState<string>();
  const [resetAssessmentModal, setResetAssessmentModal] = useState<ResetAssessmentModal>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<UsersListSearchParams>({
    assessmentSlug: assessmentSlug,
    search: undefined,
    page: 1,
    pageSize,
    organizationLevel,
    companySlug: companyVariables.slug!,
    departmentSlug: undefined,
    teamSlug: undefined,
    learnerSlug: undefined,
    completionStatus: undefined,
    sortBy: undefined,
    sortAscending: undefined,
    departmentSlugs: undefined,
    teamSlugs: undefined,
    roleSlugs: undefined,
    skillSlugs: undefined,
    countryIds: undefined,
  });

  const [state, dispatch] = useReducer(stateSetup.reducer, stateSetup.initialState);

  async function fetchPageData() {
    actions.startLoader({ path: PagePath.assessmentManagementAssignees, type: FetchType.PageFetching });

    await actions.getAssessmentAssigneeList({ assessmentSlug: assessmentSlug!, searchParams }).then(async result => {
      if (!result) setHasError(true);
      else if (hasError) setHasError(false);

      dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.PopulatePageData, payload: result });

      actions.stopLoader(PagePath.assessmentManagementAssignees);
    });
  }

  useEffect(() => {
    fetchPageData();
  }, [assessmentSlug, searchParams]);

  function onClickCancelManagingColumns() {
    dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.ManageColumns, payload: { open: false, reset: false } });
  }

  function onClickResetManagingColumns() {
    dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.ManageColumns, payload: { open: true, reset: true } });
  }

  function onClickSubmitManagingColumns(selection: ColumnConfiguration[]) {
    dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.EditColumnConfiguration, payload: selection });
    dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.ManageColumns, payload: { open: false } });
  }

  function onClickResetModalOpen(person: AssessmentAssigneeListItem) {
    if (!person) return;
    setResetAssessmentModal({ open: true, person: person });
  }

  function onClickResetModalClose() {
    setResetAssessmentModal({ open: false, person: undefined });
  }

  async function markLearnerAsComplete(id: string) {
    setLoadingLearner(id);
    await actions.completeAssigneeAssessment({ assessmentSlug: assessmentSlug!, userId: id });
    await fetchPageData();
    setLoadingLearner(void 0);
  }

  function clearFilters() {
    setSearchParams(currentState => ({ ...currentState, completionStatus: undefined, page: 1 }));
  }

  function applyFilters() {
    setSearchParams(currentState => ({ ...currentState, completionStatus: selectedStatus !== undefined ? [selectedStatus.toString()] : undefined, page: 1 }));
  }

  return (
    <Fragment>
      <PageLoaderLayer path={PagePath.assessmentManagementAssignees}>
        <div className="pageWrapper wrapper">
          <PageHeader
            breadcrumbs={[
              { name: 'Assessment Management', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}` },
              { name: state.pageData?.assessmentName ?? 'Assignees', url: location.pathname },
            ]}
            title={state.pageData?.assessmentName ?? 'Assignees'}
          />

          <div className={layoutStyles.filtersGroup} style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 15, alignItems: 'center' }}>
              <Searchfield loading={false} value={searchParams.search} onInput={search => setSearchParams({ ...searchParams, search })} />

              <Filters onClickApply={applyFilters} onClickClear={clearFilters}>
                <DropdownSelect
                  label="Status"
                  options={statuses}
                  value={selectedStatus?.toString()}
                  onChange={event => {
                    const value = event.target.value;
                    setSelectedStatus(!!value ? parseInt(value) : undefined);
                  }}
                />
              </Filters>
            </div>

            <div>
              <Button square theme="dark" type="button" onClick={() => actions.getAssessmentAssigneeListCSV(assessmentSlug!)}>
                <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                  <svg fill="none" viewBox="0 0 20 17" width={20}>
                    <path
                      fill="var(--apple)"
                      fillRule="evenodd"
                      d="M9.74069 13.0831c-.06834-.0284-.13041-.07-.18266-.1223l-3.375-3.37505a.56317.56317 0 0 1-.12209-.18272.56309.56309 0 0 1-.04287-.21553.56309.56309 0 0 1 .04287-.21553.563223.563223 0 0 1 .30481-.30481.563277.563277 0 0 1 .21553-.04287c.07396 0 .1472.01457.21553.04287a.56333.56333 0 0 1 .18272.12209l2.41425 2.41535V1.3125c0-.14918.05926-.29226.16475-.397748A.562515.562515 0 0 1 9.95628.75c.14922 0 .29222.059263.39772.164752.1055.105488.1648.248568.1648.397748v9.8921l2.4142-2.41535c.1057-.10562.2489-.16496.3983-.16496.1494 0 .2926.05934.3982.16496.1057.10562.165.24888.165.39825s-.0593.29263-.165.39825l-3.375 3.37505a.564104.564104 0 0 1-.1826.1223c-.0684.0283-.1416.0429-.21562.0429-.07399 0-.14725-.0146-.21559-.0429Zm8.25461-2.155c.1055-.1055.2486-.1648.3978-.1648s.2922.0593.3977.1648c.1055.1055.1648.2486.1648.3977v2.8125c0 .5968-.2371 1.1691-.659 1.591-.422.422-.9943.659-1.591.659H3.20557c-.59674 0-1.16904-.237-1.59099-.659-.42196-.4219-.659014-.9942-.659014-1.591v-2.8125c0-.1491.059264-.2922.164754-.3977.10549-.1055.24856-.1648.39775-.1648.14918 0 .29226.0593.39775.1648.10548.1055.16475.2486.16475.3977v2.8125c0 .2984.11852.5846.3295.7955.21098.211.49713.3295.7955.3295H16.7056c.2983 0 .5845-.1185.7955-.3295.2109-.2109.3295-.4971.3295-.7955v-2.8125c0-.1491.0592-.2922.1647-.3977Z"
                      clipRule="evenodd"
                    />
                    <path
                      fill="var(--apple)"
                      d="m9.55803 12.9608.17677-.1768.00023.0002-.177.1766Zm-3.375-3.37505-.17678.17678.17678-.17678Zm-.12209-.18272.23097-.09567-.23097.09567Zm.12209-.61378.17678.17678H6.3598l-.17677-.17678Zm.18272-.12209.09567.23097-.09567-.23097Zm.43106 0-.09567.23097.09567-.23097Zm.18272.12209.17677-.17678.00005.00004-.17682.17674Zm2.41425 2.41535h.25v.6038l-.42682-.427.17682-.1768Zm0-9.8921h-.25.25Zm.16475-.397748L9.38175.737976l.00001-.000001.17677.176777ZM9.95628.75V1 .75Zm.39772.164752.1768-.176777v.000001l-.1768.176776Zm.1648.397748h-.25.25Zm0 9.8921.1768.1768-.4268.427v-.6038h.25Zm2.4142-2.41535-.1768-.17673.0001-.00005.1767.17678Zm.7965 0-.1767.17678.1767-.17678Zm0 .7965.1768.17678-.1768-.17678Zm-3.375 3.37505-.177-.1766.0003-.0002.1767.1768Zm-.1826.1223-.0959-.231.0959.231Zm7.8234-2.155-.1768-.1768.1768.1768Zm.7955 0 .1768-.1768-.1768.1768Zm-.4942 4.8012.1767.1768-.1767-.1768ZM1.12032 10.9281l.17678.1768-.17678-.1768Zm.7955 0 .17677-.1768-.17677.1768Zm.49425 4.0057.17678-.1767-.17678.1767Zm15.09103 0-.1768-.1767.1768.1767Zm-7.76607-2.1496c.02902.0291.06351.0522.10148.0679l-.19164.4619c-.09871-.041-.18837-.101-.26384-.1767l.354-.3531ZM6.3598 9.40897l3.375 3.37503-.35355.3535-3.375-3.37497.35355-.35356Zm-.06789-.10161a.313223.313223 0 0 0 .0679.10162l-.35356.35355a.81332.81332 0 0 1-.17628-.26383l.46194-.19134Zm-.02384-.11986c0 .04113.0081.08186.02384.11986l-.46194.19134a.81315.81315 0 0 1-.0619-.3112h.5Zm.02384-.11986a.313216.313216 0 0 0-.02384.11986h-.5a.81315.81315 0 0 1 .0619-.3112l.46194.19134Zm.06789-.10161a.313206.313206 0 0 0-.06789.10161l-.46194-.19134a.81332.81332 0 0 1 .17628-.26383l.35355.35356Zm.10162-.0679a.313075.313075 0 0 0-.10161.0679l-.35356-.35356a.813213.813213 0 0 1 .26383-.17628l.19134.46194Zm.11986-.02384a.313216.313216 0 0 0-.11986.02384l-.19135-.46194a.813237.813237 0 0 1 .31121-.0619v.5Zm.11986.02384a.313216.313216 0 0 0-.11986-.02384v-.5a.81315.81315 0 0 1 .3112.0619l-.19134.46194Zm.10161.0679a.313075.313075 0 0 0-.10161-.0679l.19134-.46194a.81317.81317 0 0 1 .26382.17628l-.35355.35356Zm2.41421 2.41537L6.80271 8.96599l.35364-.35348L9.5706 11.0279l-.35364.3535Zm.42682-10.0689v9.8921h-.5V1.3125h.5Zm.09153-.22097a.31247.31247 0 0 0-.09153.22097h-.5c0-.21549.0856-.422151.23797-.574524l.35356.353554ZM9.95628 1a.31247.31247 0 0 0-.22097.09153L9.38176.737975C9.53413.585602 9.74079.5 9.95628.5V1Zm.22102.09153C10.1186 1.03292 10.0392 1 9.95628 1V.5c.21552 0 .42212.085602.57452.237975l-.3535.353555Zm.0915.22097c0-.08288-.0329-.16237-.0915-.22097l.3535-.353554c.1524.152373.238.359034.238.574524h-.5Zm0 9.8921V1.3125h.5v9.8921h-.5Zm2.841-2.23861-2.4142 2.41541-.3536-.3535 2.4142-2.41538.3536.35347Zm.2215-.0917c-.0831 0-.1628.033-.2215.09174l-.3535-.35356c.1525-.1525.3593-.23818.575-.23818v.5Zm.2215.09174c-.0588-.05874-.1385-.09174-.2215-.09174v-.5c.2157 0 .4225.08568.575.23818l-.3535.35356Zm.0917.22147c0-.08307-.033-.16273-.0917-.22147l.3535-.35356c.1525.15251.2382.35936.2382.57503h-.5Zm-.0917.22147a.313311.313311 0 0 0 .0917-.22147h.5c0 .21568-.0857.42252-.2382.57503l-.3535-.35356Zm-3.375 3.37503 3.375-3.37503.3535.35356-3.375 3.37497-.3535-.3535Zm-.1018.0681c.038-.0157.0725-.0388.1015-.0679l.354.3531c-.0754.0757-.1651.1357-.2638.1767l-.1917-.4619Zm-.11972.0239c.0411 0 .08182-.0081.11972-.0239l.1917.4619c-.0987.0409-.2045.062-.31142.062v-.5Zm-.11977-.0239c.03796.0158.07866.0239.11977.0239v.5c-.10688 0-.2127-.0211-.31141-.062l.19164-.4619Zm8.55659-1.8388c-.0829 0-.1624.033-.221.0916l-.3536-.3536c.1524-.1523.3591-.238.5746-.238v.5Zm.2209.0916c-.0586-.0586-.1381-.0916-.2209-.0916v-.5c.2155 0 .4221.0857.5745.238l-.3536.3536Zm.0916.2209c0-.0828-.033-.1623-.0916-.2209l.3536-.3536c.1524.1524.238.3591.238.5745h-.5Zm0 2.8125v-2.8125h.5v2.8125h-.5Zm-.5858 1.4143c.3751-.3751.5858-.8838.5858-1.4143h.5c0 .6631-.2634 1.299-.7323 1.7678l-.3535-.3535Zm-1.4142.5857c.5304 0 1.0391-.2107 1.4142-.5857l.3535.3535c-.4688.4689-1.1047.7322-1.7677.7322v-.5Zm-13.50003 0H16.7056v.5H3.20557v-.5Zm-1.41422-.5857c.37508.375.88378.5857 1.41422.5857v.5c-.66304 0-1.29893-.2633-1.76777-.7322l.35355-.3535Zm-.58578-1.4143c0 .5305.21071 1.0392.58578 1.4143l-.35355.3535c-.468842-.4688-.732234-1.1047-.732234-1.7678h.500004Zm0-2.8125v2.8125H.705566v-2.8125h.500004Zm.09153-.2209c-.05861.0586-.09153.1381-.09153.2209H.705566c0-.2154.085604-.4221.237976-.5745l.353558.3536Zm.22097-.0916c-.08288 0-.16237.033-.22097.0916l-.353558-.3536c.152368-.1523.359038-.238.574528-.238v.5Zm.22097.0916c-.05861-.0586-.13809-.0916-.22097-.0916v-.5c.21549 0 .42215.0857.57452.238l-.35355.3536Zm.09153.2209c0-.0828-.03293-.1623-.09153-.2209l.35355-.3536c.15237.1524.23798.3591.23798.5745h-.5Zm0 2.8125v-2.8125h.5v2.8125h-.5Zm.40273.9723c-.25787-.2578-.40273-.6076-.40273-.9723h.5c0 .2321.09218.4547.25628.6188l-.35355.3535Zm.97227.4027c-.36468 0-.71441-.1448-.97227-.4027l.35355-.3535c.16409.1641.38665.2562.61872.2562v.5Zm13.50003 0H3.20557v-.5H16.7056v.5Zm.9722-.4027c-.2578.2579-.6076.4027-.9722.4027v-.5c.232 0 .4546-.0921.6187-.2562l.3535.3535Zm.4028-.9723c0 .3647-.1449.7145-.4028.9723l-.3535-.3535c.1641-.1641.2563-.3867.2563-.6188h.5Zm0-2.8125v2.8125h-.5v-2.8125h.5Zm.0915-.2209a.31246.31246 0 0 0-.0915.2209h-.5c0-.2154.0856-.4221.2379-.5745l.3536.3536Z"
                    />
                  </svg>
                  Download CSV
                </div>
              </Button>
            </div>
          </div>

          {!!state.pageData?.list?.length && (
            <div style={{ marginTop: 30 }}>
              <Table
                footerContent={
                  <PagerConnector
                    onPageChange={page => {
                      setSearchParams({ ...searchParams, page });
                      window.scrollTo(0, 0);
                    }}
                    defaultPageNumber={searchParams.page}
                    pageCount={state.pageData.totalPages ?? 1}
                  >
                    {connector => {
                      return <Pager {...connector} />;
                    }}
                  </PagerConnector>
                }
                onConfigClicked={() => dispatch({ type: stateSetup.AssessmentAssigneesActionTypes.ManageColumns, payload: { open: true } })}
                selectableRows
                selectedRows={selectedRows}
                onRowSelection={setSelectedRows}
              >
                {state.pageData.list.map(person => {
                  const isLoading = loadingLearner === person.learnerSlug;

                  return (
                    <TableRow
                      isLoading={isLoading}
                      key={`${person.lastName}_${person.learnerSlug}`}
                      id={`row_${person.lastName}_${person.learnerSlug}`}
                      configCell={() => {
                        if (person.completionStatus === CompletionStatus.Completed || person.completionStatus === CompletionStatus.InProgress) return <KebabMenu items={[{ label: 'Reset Assessment', onClick: () => onClickResetModalOpen(person) }]} />;
                        return <KebabMenu items={[{ label: 'Mark as complete', onClick: () => markLearnerAsComplete(person.learnerSlug) }]} />;
                      }}
                    >
                      <TableColumn locked label="Name" id={'names'}>
                        <strong>
                          {person.firstName} {person.lastName}
                        </strong>
                      </TableColumn>

                      <TableColumn hidden={!state.columnConfiguration.find(i => i.key === 'jobTitle')?.selected} label="Job title" id={'jobTitle'}>
                        {person.jobTitle}
                      </TableColumn>

                      <TableColumn hidden={!state.columnConfiguration.find(i => i.key === 'status')?.selected} label="Status" id={'status'}>
                        <CompletionStatusChip completionStatus={person.completionStatus} />
                      </TableColumn>

                      <TableColumn label="Email" hidden={!state.columnConfiguration.find(i => i.key === 'email')?.selected} id={'email'}>
                        {person.email}
                      </TableColumn>

                      <TableColumn label="Department" hidden={!state.columnConfiguration.find(i => i.key === 'department')?.selected} id={'department'}>
                        {person.departmentName}
                      </TableColumn>

                      <TableColumn label="Team" hidden={!state.columnConfiguration.find(i => i.key === 'team')?.selected} id={'team'}>
                        {person.teamName}
                      </TableColumn>

                      <TableColumn label="Skill score" hidden={!state.columnConfiguration.find(i => i.key === 'skillScore')?.selected} id={'skillScore'}>
                        {companyVariables.useLevels ? (
                          <SkillLevel
                            level={person.score?.level ?? companyVariables.minLevel}
                            minLevel={companyVariables.minLevel}
                            maxLevel={companyVariables.maxLevel}
                            noLevel={!person.score}
                            dotColour={scoreLevelRAGColour(person.score?.level ?? 0, companyVariables.minLevel, companyVariables.maxLevel)}
                          />
                        ) : (
                          <SkillPercentage percentage={person.score?.percentage ?? 0} noPercentage={!person.score} barColour={scorePercentageRAGColour(person.score?.percentage ?? 0)} />
                        )}
                      </TableColumn>
                    </TableRow>
                  );
                })}
              </Table>
            </div>
          )}

          {!state.pageData?.list.length && hasError && (
            <div style={{ padding: '20px 0' }}>
              <EmptyState title="Something went wrong" subtitle="Sorry, we couldn't load the assignees for this assessment" />
            </div>
          )}

          {!state.pageData?.list.length && !hasError && (
            <div style={{ padding: '20px 0' }}>
              <EmptyState title="Nothing Found" subtitle={`No assignees were found for this assessment`} />
            </div>
          )}
        </div>
      </PageLoaderLayer>

      <Modal open={!!resetAssessmentModal?.open} onClose={onClickResetModalClose}>
        {!!resetAssessmentModal?.person?.email && (
          <ConfirmationModalLayout
            title="Reset assessment"
            titleSize="standard"
            submitButtonText="Yes, reset"
            onClickSubmit={async () => {
              if (!resetAssessmentModal?.person?.email) return;
              setLoadingLearner(resetAssessmentModal.person.learnerSlug);
              await actions.resetAssessment({ assessmentSlug: assessmentSlug!, emails: [resetAssessmentModal.person.email] }!);
              setResetAssessmentModal({ person: undefined, open: false });
              await fetchPageData();
              setLoadingLearner(void 0);
            }}
            cancelButtonText="No, cancel"
            textAlign="left"
            onClickCancel={onClickResetModalClose}
          >
            <h3 style={{ fontWeight: 400 }}>
              Are you sure you want to reset the following assessment for{' '}
              <strong>
                {resetAssessmentModal.person.firstName} {resetAssessmentModal.person.lastName}
              </strong>
            </h3>
          </ConfirmationModalLayout>
        )}
      </Modal>

      <Panel open={!!state.managingColumns} onClose={onClickCancelManagingColumns}>
        <ColumnConfiguratorWidget onReset={onClickResetManagingColumns} onCancel={onClickCancelManagingColumns} onSave={onClickSubmitManagingColumns} columns={state.columnConfiguration} />
      </Panel>
    </Fragment>
  );
}
